import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import {
  BufferTemperatureMainProperties,
  DcSensorValueProperties,
  DhwHotWaterStorageTemperatureBottom,
  DhwHotWaterStorageTemperatureTop,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  FuelCellOperatingModesActiveCommands,
  FuelCellOperatingModesActiveProperties,
  FuelCellOperatingPhase,
  FuelCellStatisticsProperties,
  HeatingFuelCellOperatingModesActive,
  IsNumberValueProperty,
} from '@vi-iot/client';

import { InstallationFeaturesStore } from '@store';

@Component({
  selector: 'app-fuel-cell-overlay',
  templateUrl: './fuel-cell-overlay.component.html',
  styleUrls: ['./fuel-cell-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FuelCellDetailsOverlayComponent implements OnInit {
  storageTankSensorBottom$: Observable<Feature<DhwHotWaterStorageTemperatureBottom, never>>;
  storageTankSensorTop$: Observable<Feature<DhwHotWaterStorageTemperatureTop, never>>;
  volumetricFlowReturn$: Observable<Feature<DcSensorValueProperties, never>>;
  fuelCellStatistics$: Observable<Feature<FuelCellStatisticsProperties, never>>;
  powerOutput$: Observable<Feature<DcSensorValueProperties, never>>;
  fuelCellReturnTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  boilerCommonSupplyTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  returnFlowTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  fuelCellActiveMode$: Observable<
    Feature<FuelCellOperatingModesActiveProperties, FuelCellOperatingModesActiveCommands>
  >;
  fuelCellOperatingPhase$: Observable<Feature<FuelCellOperatingPhase, never>>;
  fuelCellSupplyTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  burnerModulation$: Observable<Feature<IsNumberValueProperty, never>>;
  hydraulicSeparatorTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  heatingBoilerTemperature$: Observable<Feature<IsNumberValueProperty, never>>;
  bufferTemperatureMain$: Observable<Feature<BufferTemperatureMainProperties, never>>;
  fuelCellOperatingMode: keyof typeof HeatingFuelCellOperatingModesActive;

  readonly featurePropertyKey = FeaturePropertyKey;

  constructor(private installationFeaturesStore: InstallationFeaturesStore) {}

  ngOnInit(): void {
    this.storageTankSensorBottom$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.storageTankSensorBottom
    );
    this.storageTankSensorTop$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.storageTankSensorTop
    );
    this.volumetricFlowReturn$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.volumetricFlowReturn
    );
    this.fuelCellStatistics$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.fuelCellStatistics
    );
    this.powerOutput$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.powerOutput
    );
    this.fuelCellReturnTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.fuelCellReturnTemperature
    );
    this.boilerCommonSupplyTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.boilerCommonSupplyTemperature
    );
    this.returnFlowTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.returnFlowTemperature
    );
    this.fuelCellActiveMode$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature(FeatureNames.fuelCellActiveMode)
      .pipe(
        tap(
          (
            feature: Feature<
              FuelCellOperatingModesActiveProperties,
              FuelCellOperatingModesActiveCommands
            >
          ) => {
            this.fuelCellOperatingMode = feature?.properties?.value?.value;
          }
        )
      );

    this.fuelCellOperatingPhase$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.fuelCellOperatingPhase
    );
    this.fuelCellSupplyTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.fuelCellSupplyTemperature
    );
    this.burnerModulation$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.burnerModulation
    );
    this.hydraulicSeparatorTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.hydraulicSeparatorTemperature
    );
    this.heatingBoilerTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingBoilerTemperature
    );
    this.bufferTemperatureMain$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.bufferCylinderTemperatureMain
    );
  }

  onFuelCellOperatingModeChange(option: { value: string }): void {
    const operatingModeChanged = option.value !== this.fuelCellOperatingMode;
    if (operatingModeChanged) {
      this.installationFeaturesStore.updateFeature(
        FeatureNames.fuelCellActiveMode,
        option.value,
        'value',
        'mode',
        'setMode'
      );
    }
  }
}
